/**
 * Breadcrumb telemetry system for OptiBay operations
 * Provides a rolling log of key events for debugging and observability
 */

export interface Breadcrumb {
  type: string;
  details: Record<string, any>;
  ts: number;
  sessionId?: string;
}

export type BreadcrumbType = 
  | 'OB:GUARD'      // Before placement validation
  | 'OB:COMMIT'     // Successful placement
  | 'OB:FAIL'       // Invariant violation
  | 'OB:ACK'        // DB persistence confirmed
  | 'OB:SMART_ASSIGN' // Smart assign invoked
  | 'OB:OPTIPLACE'    // OptiPlace modal action
  | 'OB:ROLLOVER'     // EOD rollover action
  | 'OB:BAY_ASSIGN'   // Bay assignment change
  | 'OB:RECALC'       // Schedule recalculation
  | 'OB:DRAG'         // Manual drag operation
  | 'OB:BUMP'         // Auto-bump triggered
  | 'OB:PARKING_LOT'  // Parking lot operation
  | 'OB:GUIDE_START'  // Tutorial started
  | 'OB:GUIDE_STEP_OK' // Tutorial step completed
  | 'OB:GUIDE_PREV'   // Tutorial previous step
  | 'OB:GUIDE_SKIP'   // Tutorial step skipped
  | 'OB:GUIDE_DONE'   // Tutorial fully completed
  | 'OB:GUIDE_DISMISS' // Tutorial dismissed
  | 'OB:GUIDE_RESUME'; // Tutorial resumed

class BreadcrumbStore {
  private crumbs: Breadcrumb[] = [];
  private maxSize = 100;
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(7)}`;
  }

  log(type: BreadcrumbType, details: Record<string, any>) {
    const breadcrumb: Breadcrumb = {
      type,
      details,
      ts: Date.now(),
      sessionId: this.sessionId,
    };

    this.crumbs.push(breadcrumb);

    // Keep only last maxSize breadcrumbs
    if (this.crumbs.length > this.maxSize) {
      this.crumbs.shift();
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.log(`[${type}]`, details);
    }
  }

  get(limit?: number): Breadcrumb[] {
    if (limit) {
      return this.crumbs.slice(-limit);
    }
    return [...this.crumbs];
  }

  clear() {
    this.crumbs = [];
    this.sessionId = this.generateSessionId();
  }

  export(): string {
    return JSON.stringify({
      sessionId: this.sessionId,
      exportedAt: new Date().toISOString(),
      breadcrumbs: this.crumbs,
    }, null, 2);
  }

  getLastByType(type: BreadcrumbType, count = 5): Breadcrumb[] {
    return this.crumbs
      .filter(crumb => crumb.type === type)
      .slice(-count);
  }

  getByTimeRange(startTs: number, endTs: number): Breadcrumb[] {
    return this.crumbs.filter(crumb => 
      crumb.ts >= startTs && crumb.ts <= endTs
    );
  }
}

export const breadcrumbs = new BreadcrumbStore();

// Helper functions for common operations
export const logGuard = (details: {
  ticketId: string;
  techId: string;
  bayId?: string;
  reason: string;
}) => breadcrumbs.log('OB:GUARD', details);

export const logCommit = (details: {
  ticketId: string;
  techId: string;
  bayId?: string;
  startTime: string;
  endTime: string;
}) => breadcrumbs.log('OB:COMMIT', details);

export const logFail = (details: {
  ticketId: string;
  reason: string;
  violations?: string[];
}) => breadcrumbs.log('OB:FAIL', details);

export const logAck = (details: {
  ticketId: string;
  operation: string;
  success: boolean;
}) => breadcrumbs.log('OB:ACK', details);
